/**
 * Parseo de un Excel/CSV de eventos para el widget de Calendario (campañas comerciales
 * o eventos internos), al mismo formato que ya usan los eventos agregados a mano o
 * importados desde Nager.Date: { id, name, description, startDate, endDate }. Logica
 * pura, sin dependencias de XLSX ni del DOM, para poder testear el mapeo de columnas y
 * fechas sin un archivo real de por medio.
 */

// Encabezados aceptados por campo (comparados en minusculas, sin tildes, sin espacios extra).
const HEADER_SYNONYMS = {
  name: ['nombre', 'name', 'evento', 'event', 'titulo'],
  description: ['descripcion', 'description', 'detalle', 'nota'],
  start: ['fecha inicio', 'fechainicio', 'fecha', 'start', 'startdate', 'inicio'],
  end: ['fecha fin', 'fechafin', 'fecha termino', 'end', 'enddate', 'fin', 'termino'],
};

// eslint-disable-next-line no-misleading-character-class -- rango de marcas diacriticas combinables, para quitar tildes
const DIACRITICS_RE = /[̀-ͯ]/g;

function normalizeHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITICS_RE, ''); // quita tildes
}

/** Para cada campo (name/description/start/end), la clave real de columna que matchea, o null. */
function resolveHeaderMap(sampleRow) {
  const rawKeys = Object.keys(sampleRow || {});
  const normalizedToRaw = new Map(rawKeys.map((k) => [normalizeHeader(k), k]));
  const map = {};
  for (const [field, synonyms] of Object.entries(HEADER_SYNONYMS)) {
    const match = synonyms.find((s) => normalizedToRaw.has(s));
    map[field] = match ? normalizedToRaw.get(match) : null;
  }
  return map;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const SLASH_OR_DASH_DATE_RE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Normaliza un valor de celda de fecha a "yyyy-mm-dd" (lo que esperan los inputs
 * type="date" del editor). Acepta un objeto Date (celdas con formato fecha, XLSX.read
 * con cellDates:true), "yyyy-mm-dd" tal cual, o "dd/mm/yyyy" / "dd-mm-yyyy" (formato
 * mas comun al tipear fechas a mano en Excel, dia/mes/año). Cualquier otra cosa: null
 * (fila invalida).
 */
export function normalizeExcelDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }
  const str = String(value ?? '').trim();
  if (!str) return null;
  const isoMatch = str.match(ISO_DATE_RE);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const slashMatch = str.match(SLASH_OR_DASH_DATE_RE);
  if (slashMatch) {
    const [, d, m, y] = slashMatch;
    return `${y}-${pad2(m)}-${pad2(d)}`;
  }
  return null;
}

/**
 * rows: filas ya leidas con XLSX.utils.sheet_to_json (un objeto por fila, claves = los
 * encabezados reales del Excel). Devuelve { events, skipped, totalRows }: events en el
 * mismo formato que el resto del editor, skipped = filas descartadas (sin nombre o sin
 * fecha de inicio valida) y totalRows = filas leidas en total, para que el caller pueda
 * distinguir "nada matcheo" (todo se descarta) de "ya estaba importado" (matcheo pero
 * son duplicados) al armar el mensaje para el usuario.
 */
export function parseCalendarEventsRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return { events: [], skipped: 0, totalRows: 0 };
  const headerMap = resolveHeaderMap(list[0]);
  let skipped = 0;
  const events = [];
  list.forEach((row, idx) => {
    const name = headerMap.name ? String(row[headerMap.name] ?? '').trim() : '';
    const startDate = headerMap.start ? normalizeExcelDate(row[headerMap.start]) : null;
    if (!name || !startDate) {
      skipped += 1;
      return;
    }
    const endDateRaw = headerMap.end ? normalizeExcelDate(row[headerMap.end]) : null;
    // Fecha fin anterior a la de inicio (rango invertido, dato mal cargado en el Excel):
    // se ignora esa fecha fin en vez de dejar pasar un rango invalido, igual criterio
    // de fondo que endBeforeStart en el alta manual (ahi se bloquea antes de guardar).
    const endDate = endDateRaw && endDateRaw >= startDate ? endDateRaw : startDate;
    events.push({
      id: `ev-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      description: headerMap.description ? String(row[headerMap.description] ?? '').trim() : '',
      startDate,
      endDate,
    });
  });
  return { events, skipped, totalRows: list.length };
}
